import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

export interface FamilyContact {
  id: string;
  family_id: string;
  created_by: string;
  first_name: string;
  last_name: string;
  organization?: string;
  contact_type: 'doctor' | 'pediatrician' | 'dentist' | 'school' | 'teacher' | 'babysitter' | 'nanny' | 'daycare' | 'family_friend' | 'neighbor' | 'emergency' | 'pharmacy' | 'sports_club' | 'other' | 'nannies_helpers';
  phone_primary?: string;
  phone_secondary?: string;
  email?: string;
  address?: string;
  notes?: string;
  child_member_id?: string;
  is_emergency_contact: boolean;
  availability_notes?: string;
  created_at: string;
  updated_at: string;
  // Join with child member name
  child_member_name?: string;
  created_by_name?: string;
}

export interface CreateContactData {
  first_name: string;
  last_name: string;
  organization?: string;
  contact_type: FamilyContact['contact_type'];
  phone_primary?: string;
  phone_secondary?: string;
  email?: string;
  address?: string;
  notes?: string;
  child_member_id?: string;
  is_emergency_contact: boolean;
  availability_notes?: string;
}

export const useContacts = (familyId?: string) => {
  const [contacts, setContacts] = useState<FamilyContact[]>([]);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  const fetchContacts = async () => {
    if (!familyId) {
      setContacts([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      
      const { data: contactsData, error } = await supabase
        .from('family_contacts')
        .select('*')
        .eq('family_id', familyId)
        .order('created_at', { ascending: false });

      if (error) throw error;

      // Fetch additional data for child members and creators
      const transformedContacts = await Promise.all((contactsData || []).map(async contact => {
        let childMemberName = undefined;
        let createdByName = 'Utilisateur inconnu';

        // Fetch child member name if exists
        if (contact.child_member_id) {
          const { data: childData } = await supabase
            .from('family_members')
            .select('child_name')
            .eq('id', contact.child_member_id)
            .maybeSingle();
          
          childMemberName = childData?.child_name;
        }

        // Fetch creator name
        if (contact.created_by) {
          const { data: creatorData } = await supabase
            .from('profiles')
            .select('first_name, last_name')
            .eq('id', contact.created_by)
            .maybeSingle();
          
          if (creatorData) {
            createdByName = `${creatorData.first_name} ${creatorData.last_name}`;
          }
        }

        return {
          ...contact,
          child_member_name: childMemberName,
          created_by_name: createdByName
        };
      }));

      setContacts(transformedContacts);
    } catch (error) {
      console.error('Error fetching contacts:', error);
      toast({
        variant: "destructive",
        title: "Erreur",
        description: "Impossible de charger les contacts"
      });
    } finally {
      setLoading(false);
    }
  };

  const createContact = async (contactData: CreateContactData) => {
    if (!familyId) {
      toast({
        variant: "destructive",
        title: "Erreur",
        description: "ID de famille manquant"
      });
      return false;
    }

    try {
      // Get current user profile ID
      const { data: profile } = await supabase
        .from('profiles')
        .select('id')
        .eq('user_id', (await supabase.auth.getUser()).data.user?.id)
        .maybeSingle();

      if (!profile) {
        throw new Error('Profile not found');
      }

      const { data, error } = await supabase
        .from('family_contacts')
        .insert({
          ...contactData,
          family_id: familyId,
          created_by: profile.id
        })
        .select()
        .single();

      if (error) throw error;

      toast({
        title: "Succès",
        description: "Contact créé avec succès"
      });

      fetchContacts(); // Refresh the list
      return true;
    } catch (error) {
      console.error('Error creating contact:', error);
      toast({
        variant: "destructive",
        title: "Erreur",
        description: "Impossible de créer le contact"
      });
      return false;
    }
  };

  const updateContact = async (contactId: string, contactData: Partial<CreateContactData>) => {
    try {
      const { error } = await supabase
        .from('family_contacts')
        .update(contactData)
        .eq('id', contactId);

      if (error) throw error;

      toast({
        title: "Succès",
        description: "Contact mis à jour avec succès"
      });

      fetchContacts(); // Refresh the list
      return true;
    } catch (error) {
      console.error('Error updating contact:', error);
      toast({
        variant: "destructive",
        title: "Erreur",
        description: "Impossible de mettre à jour le contact"
      });
      return false;
    }
  };

  const deleteContact = async (contactId: string) => {
    try {
      const { error } = await supabase
        .from('family_contacts')
        .delete()
        .eq('id', contactId);

      if (error) throw error;

      toast({
        title: "Succès",
        description: "Contact supprimé avec succès"
      });

      fetchContacts(); // Refresh the list
      return true;
    } catch (error) {
      console.error('Error deleting contact:', error);
      toast({
        variant: "destructive",
        title: "Erreur",
        description: "Impossible de supprimer le contact"
      });
      return false;
    }
  };

  const getEmergencyContacts = () => {
    return contacts.filter(contact => contact.is_emergency_contact);
  };

  const getContactsByChild = (childMemberId: string) => {
    return contacts.filter(contact => contact.child_member_id === childMemberId);
  };

  const getContactsByType = (type: FamilyContact['contact_type']) => {
    return contacts.filter(contact => contact.contact_type === type);
  };

  useEffect(() => {
    fetchContacts();
  }, [familyId]);

  return {
    contacts,
    loading,
    createContact,
    updateContact,
    deleteContact,
    refreshContacts: fetchContacts,
    getEmergencyContacts,
    getContactsByChild,
    getContactsByType
  };
};